import { COULOMB_CONSTANT } from "@/lib/tool-kit/physics-fields/constants";
import type {
  CoulombSolution,
  CoulombTarget,
  FieldTarget,
  PointFieldSolution,
  SuperpositionSolution,
} from "@/lib/tool-kit/physics-fields/coulomb";
import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";

/** Equations with the numbers substituted, in SI. Symbols are not translated. */

/** kₑ, or (kₑ / εᵣ) in a medium, written with its number. */
function kText(fmt: NumberFormatter, permittivity: number): string {
  return permittivity === 1
    ? formatEcho(fmt, COULOMB_CONSTANT)
    : `(${formatEcho(fmt, COULOMB_CONSTANT)} / ${formatEcho(fmt, permittivity)})`;
}

export function coulombWorking(
  target: CoulombTarget,
  s: CoulombSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const k = kText(fmt, s.permittivity);
  const vacuum = s.permittivity === 1;
  if (target === "q1" || target === "q2") {
    const solvedName = target === "q1" ? "q₁" : "q₂";
    const otherName = target === "q1" ? "q₂" : "q₁";
    const other = target === "q1" ? s.q2 : s.q1;
    const solved = target === "q1" ? s.q1 : s.q2;
    return [
      `${solvedName} = F r²${vacuum ? "" : " εᵣ"} / (kₑ ${otherName})`,
      `${solvedName} = ${e(s.force)} × (${e(s.distance)})² / (${k} × ${e(other)}) = ${r(solved, "C")}`,
    ];
  }
  if (target === "distance") {
    return [
      `r = √(kₑ q₁ q₂ / ${vacuum ? "F" : "(εᵣ F)"})`,
      `r = √(${k} × ${e(s.q1)} × ${e(s.q2)} / ${e(s.force)}) = ${r(s.distance, "m")}`,
    ];
  }
  return [
    `F = kₑ q₁ q₂ / ${vacuum ? "r²" : "(εᵣ r²)"}`,
    `F = ${k} × ${e(s.q1)} × ${e(s.q2)} / (${e(s.distance)})² = ${r(s.force, "N")}`,
  ];
}

export function pointFieldWorking(
  target: FieldTarget,
  s: PointFieldSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const k = kText(fmt, s.permittivity);
  const vacuum = s.permittivity === 1;
  const lines: string[] = [];
  if (target === "charge") {
    lines.push(
      `|q| = E r²${vacuum ? "" : " εᵣ"} / kₑ`,
      `|q| = ${e(s.field)} × (${e(s.distance)})² / ${k} = ${r(Math.abs(s.charge), "C")}`
    );
  } else if (target === "distance") {
    lines.push(
      `r = √(kₑ |q| / ${vacuum ? "E" : "(εᵣ E)"})`,
      `r = √(${k} × ${e(Math.abs(s.charge))} / ${e(s.field)}) = ${r(s.distance, "m")}`
    );
  } else {
    lines.push(
      `E = kₑ |q| / ${vacuum ? "r²" : "(εᵣ r²)"}`,
      `E = ${k} × ${e(Math.abs(s.charge))} / (${e(s.distance)})² = ${r(s.field, "N/C")}`
    );
  }
  lines.push(
    `V = kₑ q / ${vacuum ? "r" : "(εᵣ r)"} = ${k} × ${e(s.charge)} / ${e(s.distance)} = ${r(s.potential, "V")}`
  );
  return lines;
}

export function superpositionWorking(
  s: SuperpositionSolution,
  testCharge: number | null,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const lines = [
    "E = Σ kₑ qᵢ r̂ᵢ / (εᵣ rᵢ²), added as vectors",
    ...s.contributions.map(
      (c, i) =>
        `E${i + 1} = (${formatSig(fmt, c.ex, sig)}, ${formatSig(fmt, c.ey, sig)}) N/C  [|E${i + 1}| = ${r(c.magnitude, "N/C")} at ${r(c.distance, "m")}]`
    ),
    `Eₓ = ${r(s.ex, "N/C")},  E_y = ${r(s.ey, "N/C")}`,
    `|E| = √(Eₓ² + E_y²) = ${r(s.magnitude, "N/C")},  θ = ${formatSig(fmt, s.angle, sig)}°`,
  ];
  if (s.force !== null && testCharge !== null) {
    lines.push(
      `F = q₀ E = ${e(testCharge)} × ${e(s.magnitude)} = ${r(s.force.magnitude, "N")}  at ${formatSig(fmt, s.force.angle, sig)}°`
    );
  }
  lines.push(`V = Σ kₑ qᵢ / (εᵣ rᵢ) = ${r(s.potential, "V")}`);
  return lines;
}
